import { useState, useCallback } from 'react';

import { apiRequest, ApiRequestOptions } from '../../../http';

export interface UseApiRequestState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

export interface UseApiRequestReturn<T> extends UseApiRequestState<T> {
  execute: (options: ApiRequestOptions) => Promise<T | null>;
  reset: () => void;
}

/**
 * Custom hook for making API requests with loading and error states
 */
export const useApiRequest = <T = any>(): UseApiRequestReturn<T> => {
  const [state, setState] = useState<UseApiRequestState<T>>({
    data: null,
    loading: false,
    error: null,
  });

  const execute = useCallback(async (options: ApiRequestOptions): Promise<T | null> => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const response = await apiRequest<T>(options);
      setState({
        data: response,
        loading: false,
        error: null,
      });
      return response;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      setState({
        data: null,
        loading: false,
        error: errorMessage,
      });
      return null;
    }
  }, []);

  const reset = useCallback(() => {
    setState({
      data: null,
      loading: false,
      error: null,
    });
  }, []);

  return {
    ...state,
    execute,
    reset,
  };
};

export default useApiRequest;
